import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Five account types. role = saved primary_role.
// joins = this type joins an existing org with a code (Trainer at barn, Boarder).
// controls = this type runs their own org (Barn Owner, Mobile Trainer).
const TYPES = [
  { val: 'owner', icon: '🐴', label: 'Horse Owner / Rider', sub: 'Manage my own horses', dest: '/horses/add' },
  { val: 'barn_owner', icon: '🏠', label: 'Barn Owner', sub: 'I run a barn and manage members', dest: '/', controls: true },
  { val: 'trainer', icon: '🏇', label: 'Trainer', sub: 'I train out of a barn', dest: '/', joins: true },
  { val: 'mobile_trainer', icon: '💼', label: 'Mobile Trainer', sub: 'I travel to barns & homes', dest: '/', controls: true },
  { val: 'boarder', icon: '🐎', label: 'Boarder', sub: 'I board my horse at a barn', dest: '/', joins: true },
  { val: 'barn_staff', icon: '🧤', label: 'Barn Staff', sub: 'I work at a barn — groom, stable hand, working student', dest: '/staff', joins: true },
];

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('type'); // 'type' -> 'form' -> 'sent'
  const [sentTo, setSentTo] = useState('');
  const [joinedOrg, setJoinedOrg] = useState(null);
  const [resending, setResending] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', confirm: '', invite_code: '' });
  const [loading, setLoading] = useState(false);

  const set = useCallback((field) => (e) => { const v = e.target.value; setForm(p => ({ ...p, [field]: v })); }, []);

  const pickType = (type) => { setChosen(type); setStep('form'); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
    setLoading(true);
    try {
      // Map mobile_trainer/trainer to backend primary_role 'trainer'; boarder to 'owner'
      const roleMap = { owner: 'owner', barn_owner: 'barn_owner', trainer: 'trainer', mobile_trainer: 'trainer', boarder: 'owner', barn_staff: 'owner' };
      const data = await register({
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, password: form.password,
        primary_role: roleMap[chosen.val],
        account_type: chosen.val,
        invite_code: chosen.joins ? form.invite_code.trim() : '',
      });

      setSentTo(data?.email || form.email);
      setJoinedOrg(data?.joined_organization || null);
      setStep('sent');
      setLoading(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cr-primary)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '40px 24px 20px', textAlign: 'center' }}>
        <h1 style={{ color: 'white', fontSize: '24px', fontWeight: '700' }}>Connected Rider</h1>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '4px' }}>Horse care, community & marketplace</p>
      </div>

      <div style={{ background: 'white', borderRadius: '24px 24px 0 0', padding: '28px 24px 40px', flex: 1 }}>

        {step === 'type' && (
          <>
            <h2 style={{ marginBottom: 6, textAlign: 'center' }}>Choose your account type</h2>
            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              This sets up your experience. You can do more later — nothing is locked.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {TYPES.map(t => (
                <button key={t.val} onClick={() => pickType(t)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px', background: 'white', border: '1.5px solid var(--border-color)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                  <span style={{ fontSize: 28 }}>{t.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.sub}</div>
                  </div>
                  <span style={{ color: 'var(--text-muted)', fontSize: 20 }}>›</span>
                </button>
              ))}
            </div>
            <button onClick={() => navigate('/welcome')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', marginTop: 20, width: '100%' }}>‹ Back</button>
          </>
        )}

        {step === 'sent' && (
          <>
            <div style={{ textAlign: 'center', fontSize: 44, marginBottom: 6 }}>&#128231;</div>
            <h2 style={{ marginBottom: 10, textAlign: 'center' }}>Check your email</h2>
            <p style={{ fontSize: 15, textAlign: 'center', marginBottom: 6 }}>
              We sent a confirmation link to<br /><strong>{sentTo}</strong>
            </p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 18 }}>
              Open it to finish setting up your account. <strong>You won't be able to sign in until you do.</strong>
            </p>
            {joinedOrg && (
              <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--cr-primary-light)', marginBottom: 16, fontSize: 13, textAlign: 'center' }}>
                Your request to join <strong>{joinedOrg}</strong> was sent. A manager will approve it.
              </div>
            )}
            <ol style={{ fontSize: 13, color: 'var(--text-secondary)', paddingLeft: 20, marginBottom: 20, lineHeight: 1.7 }}>
              <li>Open your inbox and look for <strong>Connected Rider</strong></li>
              <li>Tap <strong>Confirm my email</strong></li>
              <li>Come back and sign in</li>
            </ol>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 16 }}>
              Nothing there? Check your spam folder — the link expires in 48 hours.
            </p>
            <button className="btn btn-secondary btn-full" disabled={resending} onClick={async () => {
              setResending(true);
              try {
                const api = (await import('../utils/api')).default;
                const { data } = await api.post('/auth/resend-verification', { email: sentTo });
                toast.success(data.message || 'Sent');
              } catch { toast.error('Could not send. Try again shortly.'); }
              finally { setResending(false); }
            }}>
              {resending ? 'Sending...' : 'Send the link again'}
            </button>
            <button className="btn btn-primary btn-full" style={{ marginTop: 10 }} onClick={() => navigate('/login')}>
              Go to sign in
            </button>
          </>
        )}

        {step === 'form' && chosen && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 26 }}>{chosen.icon}</span>
              <div>
                <h2 style={{ fontSize: 17 }}>{chosen.label}</h2>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{chosen.sub}</p>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div className="form-group">
                  <label className="form-label">First name</label>
                  <input className="form-input" type="text" placeholder="Sarah" value={form.first_name} onChange={set('first_name')} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Last name</label>
                  <input className="form-input" type="text" placeholder="Jones" value={form.last_name} onChange={set('last_name')} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} required />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" placeholder="At least 8 characters" value={form.password} onChange={set('password')} required />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm password</label>
                <input className="form-input" type="password" placeholder="Repeat your password" value={form.confirm} onChange={set('confirm')} required />
              </div>

              {chosen.joins && (
                <div className="form-group">
                  <label className="form-label">Barn invite code</label>
                  <input className="form-input" value={form.invite_code} onChange={e => setForm(p => ({ ...p, invite_code: e.target.value.toUpperCase() }))} placeholder="Enter the code from your barn" style={{ textTransform: 'uppercase', letterSpacing: 2 }} autoComplete="off" />
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                    Your barn manager can give you a code or an invite link. You can also add this later from the Barns page.
                  </p>
                </div>
              )}

              <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading} style={{ marginTop: 8 }}>
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </form>
            <button onClick={() => setStep('type')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', marginTop: 16, width: '100%' }}>‹ Choose a different type</button>
          </>
        )}

        <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '11px', color: 'var(--text-muted)' }}>
          By creating an account you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
